/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, Navigate, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

//@ts-ignore
import Loader from '@mindtickle/loader';

import { useHeaderOffset } from '~/components/Header';
import useManagerCommandCenter from '~/components/ManagerCommandCenter/hooks/useManagerCommandCenter';
import { LEGACY_IFRAME_TITLE, PATH_NAMES, IFRAME_ROUTE_MESSAGE_EVENT_NAME } from './constants';
import { LEGACY_HASH_TO_ROUTE_MAP } from '../helpers';

const StyledIframe = styled.iframe`
  border: none;
  height: ${({ offset }: { offset: number }) => `calc(100vh - ${offset}px)`};
  display: block;
`;

const StyledLoader = styled(Loader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const LegacyAppRenderer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hash } = location;
  const defaultLoadedIframeHash = useRef(hash);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { mccAsHomepage } = useManagerCommandCenter();

  const [isIframeLoaded, updateIframeLoadingState] = useState(false);
  const [iframeSourceHash, updateIframeSourceHash] = useState(defaultLoadedIframeHash.current);

  const headerOffset = useHeaderOffset();

  const onWatchEventHandler = useCallback(
    ({ origin, data: { operation, data, url, program_id } }) => {
      if (origin === window.location.origin) {
        switch (operation) {
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.HOME: {
            navigate(PATH_NAMES.DASHBOARD, { replace: true });
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.WIDGET: {
            navigate(`${PATH_NAMES.WIDGET}?id=${data.id}`, { replace: true });
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.LEGACY: {
            // update the app route with the new hash of iframe
            let iframeHash = data.hash;
            iframeHash = iframeHash.startsWith('#') ? iframeHash : `#${iframeHash}`;
            window.location.hash !== iframeHash && (window.location.hash = iframeHash);
            updateIframeSourceHash(iframeHash);
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.NAVIGATE_TO_URL: {
            // redirect the app with the new url
            window.location.replace(url);
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.NAVIGATE: {
            // navigate the app with the new url
            navigate(url, { replace: true });
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.PROGRAMS_NAVIGATE: {
            navigate(LEGACY_HASH_TO_ROUTE_MAP[PATH_NAMES.PROGRAMS](url), {
              replace: true,
            });
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.PROGRAM_NAVIGATE: {
            //We are updating url path from /modules to /overview as we want to make it consistent redirection in series invitation email
            //Bug: https://mindtickle.atlassian.net/browse/GEPI-3131
            navigate(`${PATH_NAMES.PROGRAMS}/${program_id}/overview`, { replace: true });
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.NAVIGATE_BACK: {
            // redirect to the previous route
            navigate(-1);
            break;
          }
          case IFRAME_ROUTE_MESSAGE_EVENT_NAME.RELOAD: {
            window.location.reload();
            break;
          }
          default: {
            // do nothing
          }
        }
      }
    },
    [navigate]
  );

  const onHashChangeEvent = useCallback(() => {
    const currentLocationHash = window.location.hash;
    if (currentLocationHash !== iframeSourceHash) {
      const iframeElement = iframeRef.current;
      const iframeWin = iframeElement?.contentWindow;
      iframeWin?.location.replace(`//${window.location.host}/legacy${currentLocationHash}`);
    }
  }, [iframeSourceHash]);

  useEffect(() => {
    window.addEventListener('hashchange', onHashChangeEvent);
    return () => window.removeEventListener('hashchange', onHashChangeEvent);
  }, [onHashChangeEvent]);

  useEffect(() => {
    window.addEventListener('message', onWatchEventHandler);
    return () => window.removeEventListener('message', onWatchEventHandler);
  }, [onWatchEventHandler]);

  const onIframeLoad = useCallback(() => {
    updateIframeLoadingState(true);
  }, [updateIframeLoadingState]);

  if (!hash) {
    return (
      <Navigate to={mccAsHomepage ? PATH_NAMES.COMMAND_CENTER : PATH_NAMES.DASHBOARD} replace />
    );
  }

  return (
    <>
      <StyledIframe
        title={LEGACY_IFRAME_TITLE}
        src={`//${window.location.host}/legacy${defaultLoadedIframeHash.current}`}
        onLoad={onIframeLoad}
        width="100%"
        key={defaultLoadedIframeHash.current}
        offset={headerOffset}
        name={LEGACY_IFRAME_TITLE}
        ref={iframeRef}
      />
      {!isIframeLoaded ? <StyledLoader /> : null}
    </>
  );
};

export default LegacyAppRenderer;
